import { classSellsMonthly } from '@/utils/programs/classSellsMonthly';
import { FIXED_TERM_NOT_OFFERED_MESSAGE } from '@/utils/billing/firstClassDeferral';

/**
 * Does this class actually sell the enrollment type a request asks for?
 *
 * The server decides what is being sold, never the request.
 *   - monthly:    the class sells monthly (classSellsMonthly) and is not a
 *                 session program.
 *   - fixed_term: the class is a session program.
 *   - drop_in:    drop-ins are on (a missing flag means on, the column default).
 *
 * The public class page picks its options from billing_type in
 * /api/public/programs/[programType]/[programId] (an Open Gym page shows only
 * drop-ins). That agrees with the rules above except for a non-session class
 * with monthly AND drop-ins both off, where billing_type falls back to
 * 'monthly'; there is no monthly price on such a class, so it is refused here.
 * classOffersAnyEnrollment names that class: the class form and the class
 * save routes refuse it, and the class page shows it as closed (#5302).
 *
 * A request for anything else would land on a price the class never set,
 * which reads as $0.
 */
export type ClassOfferRow = {
  is_open_gym?: boolean | null;
  monthly_membership_enabled?: boolean | null;
  drop_in_enabled?: boolean | null;
  is_session_based?: boolean | null;
};

export type ClassOfferRefusal = {
  error: string;
  code: 'MONTHLY_NOT_OFFERED' | 'FIXED_TERM_NOT_OFFERED' | 'DROP_IN_NOT_OFFERED' | 'ENROLLMENT_TYPE_NOT_OFFERED';
};

export const MONTHLY_NOT_OFFERED_MESSAGE = "This class doesn't offer monthly enrollment.";
export const DROP_IN_NOT_OFFERED_MESSAGE = "Drop-ins aren't available for this class.";

export function classOffersEnrollmentType(row: ClassOfferRow, enrollmentType: string): ClassOfferRefusal | null {
  const isSession = row.is_session_based === true;
  switch (enrollmentType) {
    case 'monthly':
      return classSellsMonthly(row)
        ? null
        : { error: MONTHLY_NOT_OFFERED_MESSAGE, code: 'MONTHLY_NOT_OFFERED' };
    case 'fixed_term':
      return isSession ? null : { error: FIXED_TERM_NOT_OFFERED_MESSAGE, code: 'FIXED_TERM_NOT_OFFERED' };
    case 'drop_in':
      return (row.drop_in_enabled ?? true)
        ? null
        : { error: DROP_IN_NOT_OFFERED_MESSAGE, code: 'DROP_IN_NOT_OFFERED' };
    default:
      return { error: "This class doesn't offer that kind of enrollment.", code: 'ENROLLMENT_TYPE_NOT_OFFERED' };
  }
}

/**
 * #5654: a regular Ongoing Class whose "Offer Monthly Membership" is off — the
 * one state the drop-in-only switch creates. Session programs and Open Gym never
 * sell monthly either, but their waitlists and staff flows predate the switch
 * (their waitlist rows carry the 'monthly' column default), so they are left out.
 */
export function classIsDropInOnly(row: ClassOfferRow): boolean {
  return row.is_session_based !== true && row.is_open_gym !== true && row.monthly_membership_enabled === false;
}

/** classIsDropInOnly for a class id. 'read_failed' lets the caller fail closed. */
export async function readClassIsDropInOnly(
  client: { from: (table: string) => any },
  programId: string,
): Promise<boolean | 'read_failed'> {
  const { data, error } = await client
    .from('classes')
    .select('is_open_gym, monthly_membership_enabled, is_session_based')
    .eq('id', programId)
    .maybeSingle();
  if (error) return 'read_failed';
  return data ? classIsDropInOnly(data as ClassOfferRow) : false;
}

const ENROLLMENT_TYPES = ['monthly', 'fixed_term', 'drop_in'] as const;

/** False when a class sells nothing at all: a non-session class with monthly and drop-ins both off. */
export function classOffersAnyEnrollment(row: ClassOfferRow): boolean {
  return ENROLLMENT_TYPES.some((type) => classOffersEnrollmentType(row, type) === null);
}

export const NO_SIGN_UP_OPTION_MESSAGE = 'Turn on Offer Monthly Membership or Allow Drop-Ins.';
